'use client';
import { useEffect, useRef } from 'react';
import Link from 'next/link';

interface ServiceItem {
  label: string;
  href: string;
}

interface ServiceColumn {
  id: string;
  title: string;
  items: ServiceItem[];
}

interface ServicesMegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const columns: ServiceColumn[] = [
  {
    id: 'strategy-audits',
    title: 'Strategy & Audits',
    items: [
      { label: 'Growth Bottleneck Audit', href: '/services/strategy' },
      { label: 'Operational Strategy', href: '/services/operational-strategy-consultation' },
      { label: 'Market Research', href: '/services/market-research' },
      { label: 'Offer & Pricing Review', href: '/services/pricing-analysis' },
    ],
  },
  {
    id: 'growth-demand',
    title: 'Growth & Demand',
    items: [
      { label: 'Paid Search', href: '/services/paid-search' },
      { label: 'Paid Social', href: '/services/paid-social' },
      { label: 'SEO / GEO / AEO', href: '/services/seo' },
      { label: 'Funnel Strategy', href: '/lp/strategy-sprint' },
    ],
  },
  {
    id: 'systems-execution',
    title: 'Systems & Execution',
    items: [
      { label: 'AI Automation', href: '/services/internal-operational-structure' },
      { label: 'CRM / Follow-Up Systems', href: '/services/lifecycle-marketing' },
      { label: 'Content Systems', href: '/services/content' },
      { label: 'Lifecycle Marketing', href: '/services/lifecycle-marketing' },
    ],
  },
  {
    id: 'brand-web',
    title: 'Brand & Web',
    items: [
      { label: 'Website & Funnel Build', href: '/services/web-design' },
      { label: 'Branding & Creative', href: '/services/branding-design' },
      { label: 'Content Production', href: '/services/content' },
      { label: 'Pitch Decks', href: '/services/pitch-deck-presentation' },
    ],
  },
];

export default function ServicesMegaMenu({ isOpen, onClose }: ServicesMegaMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute top-full left-0 right-0 mt-0 bg-surface-1 border-t border-border shadow-xl z-50"
      style={{ animation: 'fadeIn 0.15s ease-out forwards' }}
    >
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {/* 4-column grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {columns.map((col) => (
            <div key={col.id}>
              <h3 className="text-[9px] uppercase tracking-ultra-wide text-primary font-semibold border-b border-border pb-2 mb-4">
                {col.title}
              </h3>
              <ul className="space-y-2.5">
                {col.items.map((item) => (
                  <li key={item.href + item.label}>
                    <Link
                      href={item.href}
                      onClick={onClose}
                      className="text-[11px] text-muted-foreground hover:text-foreground transition-colors leading-snug block"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom CTA row */}
        <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
          <Link
            href="/services/menu"
            onClick={onClose}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-foreground hover:text-primary transition-colors font-semibold group"
          >
            <span>View All Services</span>
            <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
          <Link
            href="/book"
            onClick={onClose}
            className="px-5 py-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest hover:bg-primary/90 transition-colors font-semibold"
          >
            Book a Sprint
          </Link>
        </div>
      </div>
    </div>
  );
}